import React, { useRef, useEffect, useState } from 'react';
import { Canvas2DService } from '../services/Canvas2DService';
import { MotifPersonnalise, OutilDessin } from '../types';
import './ModalEditeur2D.css';

interface ModalEditeur2DProps {
  isOpen: boolean;
  onClose: () => void;
  onSauvegarder: (nom: string, motifData?: MotifPersonnalise) => void;
  motifsPersonnalises: MotifPersonnalise[];
  onChargerMotif: (motif: MotifPersonnalise) => void;
  onSupprimerMotif: (id: string) => void;
}

/**
 * Composant modal pour l'éditeur 2D de motifs personnalisés
 */
export const ModalEditeur2D: React.FC<ModalEditeur2DProps> = ({
  isOpen,
  onClose,
  onSauvegarder,
  motifsPersonnalises,
  onChargerMotif,
  onSupprimerMotif
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasServiceRef = useRef<Canvas2DService | null>(null);
  const [nomMotif, setNomMotif] = useState('');
  const [motifEnEdition, setMotifEnEdition] = useState<MotifPersonnalise | null>(null);
  const [rechercheMotif, setRechercheMotif] = useState('');
  const [pageActuelle, setPageActuelle] = useState(1);
  const [motifsParPage] = useState(6);
  const [vueGrille, setVueGrille] = useState(true);
  const [outilActuel, setOutilActuel] = useState<OutilDessin>(OutilDessin.CRAYON);
  const [accrocheActive, setAccrocheActive] = useState<boolean>(true);


  // Initialisation du canvas 2D
  useEffect(() => {
    if (isOpen && canvasRef.current && !canvasServiceRef.current) {
      try {
        canvasServiceRef.current = new Canvas2DService(canvasRef.current);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation du canvas 2D:', error);
      }
    }

    return () => {
      if (!isOpen && canvasServiceRef.current) {
        canvasServiceRef.current.dispose();
        canvasServiceRef.current = null;
      }
    };
  }, [isOpen]);

  // Mise à jour de la pagination quand la liste des motifs change
  useEffect(() => {
    // Réinitialiser à la première page si la liste des motifs change
    setPageActuelle(1);
  }, [motifsPersonnalises.length]);

  const handleSauvegarder = () => {
    if (nomMotif.trim() && canvasServiceRef.current) {
      // Récupérer les données du canvas et les sauvegarder
      const motifData = canvasServiceRef.current.exporterMotif(nomMotif.trim());
      
      // Si on édite un motif existant, conserver son ID
      if (motifEnEdition) {
        motifData.id = motifEnEdition.id;
      }
      
      onSauvegarder(nomMotif.trim(), motifData);
      setNomMotif(''); // Réinitialiser le champ
      setMotifEnEdition(null); // Réinitialiser l'édition
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Filtrage et pagination des motifs
  const motifsFiltres = motifsPersonnalises.filter(motif => 
    motif.nom.toLowerCase().includes(rechercheMotif.toLowerCase())
  );
  
  const indexDebut = (pageActuelle - 1) * motifsParPage;
  const indexFin = indexDebut + motifsParPage;
  const motifsAffiches = motifsFiltres.slice(indexDebut, indexFin);
  const nombrePages = Math.ceil(motifsFiltres.length / motifsParPage);

  const changerPage = (nouvellePage: number) => {
    setPageActuelle(Math.max(1, Math.min(nouvellePage, nombrePages)));
  };

  // Fonction pour changer d'outil de dessin
  const changerOutil = (outil: OutilDessin) => {
    setOutilActuel(outil);
    if (canvasServiceRef.current) {
      canvasServiceRef.current.changerOutil(outil);
    }
  };

  // Fonction pour supprimer la sélection
  const supprimerSelection = () => {
    if (canvasServiceRef.current) {
      canvasServiceRef.current.supprimerSelection();
    }
  };

  // Fonction pour basculer l'accroche magnétique
  const toggleAccroche = () => {
    if (canvasServiceRef.current) {
      canvasServiceRef.current.toggleAccroche();
      setAccrocheActive(canvasServiceRef.current.getAccrocheActive());
    }
  };



  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Éditeur de motif personnalisé</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal-body">
          {/* Section principale avec canvas */}
          <div className="section-principale">
            {/* Canvas de dessin */}
            <div className="canvas-container">
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                className="canvas-dessin"
              />
            </div>
            
            {/* Contrôles du canvas */}
            <div className="canvas-controles">
              {/* Outils de dessin */}
              <div className="outils-dessin">
                <button
                  onClick={() => changerOutil(OutilDessin.CRAYON)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.CRAYON ? 'actif' : ''}`}
                  title="Outil crayon"
                >
                  ✏️ Crayon
                </button>
                <button
                  onClick={() => changerOutil(OutilDessin.LIGNE)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.LIGNE ? 'actif' : ''}`}
                  title="Outil ligne droite"
                >
                  📏 Ligne
                </button>
                <button
                  onClick={() => changerOutil(OutilDessin.BEZIER)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.BEZIER ? 'actif' : ''}`}
                  title="Outil courbe de Bézier"
                >
                  🎨 Bézier
                </button>
                <button
                  onClick={() => changerOutil(OutilDessin.SELECTION)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.SELECTION ? 'actif' : ''}`}
                  title="Outil de sélection"
                >
                  👆 Sélection
                </button>
              </div>
              
              {/* Actions */}
              <div className="actions-canvas">
                <button
                  onClick={supprimerSelection}
                  className="bouton-canvas"
                  title="Supprimer la sélection"
                >
                  🗑️ Supprimer
                </button>
                <button
                  onClick={() => canvasServiceRef.current?.annuler()}
                  className="bouton-canvas"
                >
                  ↶ Annuler
                </button>
                <button
                  onClick={() => canvasServiceRef.current?.effacer()}
                  className="bouton-canvas"
                >
                  🧹 Effacer tout
                </button>
                <button
                  onClick={() => canvasServiceRef.current?.toggleGrille()}
                  className="bouton-canvas"
                >
                  ⊞ Grille
                </button>
                <button
                  onClick={toggleAccroche}
                  className={`bouton-canvas ${accrocheActive ? 'actif' : ''}`}
                  title="Accroche magnétique"
                >
                  🧲 Accroche
                </button>
              </div>
              

              
              {/* Sauvegarde du motif */}
              <div className="sauvegarde-inline">
                <input
                  type="text"
                  value={nomMotif}
                  onChange={(e) => setNomMotif(e.target.value)}
                  placeholder="Nom du motif..."
                  className="input-nom-motif"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSauvegarder();
                    }
                  }}
                />
                <button
                  onClick={handleSauvegarder}
                  disabled={!nomMotif.trim()}
                  className="bouton-canvas bouton-primary"
                >
                  💾 Sauvegarder
                </button>
              </div>
            </div>
          </div>
          
          {/* Panneau latéral des motifs */}
          <div className="panneau-motifs">
            <div className="gestion-header">
              <h4>Motifs ({motifsFiltres.length})</h4>
              <div className="gestion-controles">
                <button
                  onClick={() => setVueGrille(!vueGrille)}
                  className="bouton-vue"
                  title={vueGrille ? 'Vue liste' : 'Vue grille'}
                >
                  {vueGrille ? '☰' : '⊞'}
                </button>
              </div>
            </div>
            
            {/* Barre de recherche */}
            <div className="recherche-section">
              <input
                type="text"
                value={rechercheMotif}
                onChange={(e) => {
                  setRechercheMotif(e.target.value);
                  setPageActuelle(1); // Retour à la première page lors de la recherche
                }}
                placeholder="Rechercher un motif..."
                className="input-recherche"
              />
              {rechercheMotif && (
                <button
                  onClick={() => {
                    setRechercheMotif('');
                    setPageActuelle(1);
                  }}
                  className="bouton-effacer-recherche"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Liste des motifs sauvegardés */}
            {motifsFiltres.length > 0 ? (
              <div className="motifs-existants">
                <div className={`liste-motifs-canvas ${vueGrille ? 'vue-grille' : 'vue-liste'}`}>
                  {motifsAffiches.map(motif => (
                    <div key={motif.id} className="motif-canvas-item">
                      {vueGrille && (
                        <div className="motif-miniature">
                          <img 
                            src={Canvas2DService.genererMiniature(motif, 120)} 
                            alt={`Miniature de ${motif.nom}`}
                            className="miniature-image"
                          />
                        </div>
                      )}
                      <div className="motif-info">
                        <span className="motif-nom" title={motif.nom}>{motif.nom}</span>
                        <span className="motif-details">
                          {motif.lignes.length} ligne{motif.lignes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="motif-actions">
                        <button
                          onClick={() => {
                            // Charger le motif dans l'éditeur
                            if (canvasServiceRef.current) {
                              canvasServiceRef.current.importerMotif(motif);
                            }
                            setNomMotif(motif.nom);
                            setMotifEnEdition(motif);
                          }}
                          className="bouton-motif bouton-editer"
                          title="Éditer ce motif"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => {
                            // Charger le motif et fermer la modale
                            onChargerMotif(motif);
                            onClose();
                          }}
                          className="bouton-motif bouton-charger"
                          title="Utiliser ce motif"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Êtes-vous sûr de vouloir supprimer le motif \"${motif.nom}\" ?`)) {
                              onSupprimerMotif(motif.id);
                            }
                          }}
                          className="bouton-motif bouton-supprimer"
                          title="Supprimer ce motif"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination */}
                {nombrePages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => changerPage(pageActuelle - 1)}
                      disabled={pageActuelle === 1}
                      className="bouton-page"
                    >
                      ‹
                    </button>
                    
                    <span className="info-page">
                      Page {pageActuelle} sur {nombrePages}
                    </span>
                    
                    <button
                      onClick={() => changerPage(pageActuelle + 1)}
                      disabled={pageActuelle === nombrePages}
                      className="bouton-page"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="aucun-motif">
                {rechercheMotif ? (
                  <p>Aucun motif trouvé pour "{rechercheMotif}"</p>
                ) : (
                  <p>Aucun motif sauvegardé. Dessinez votre premier motif !</p>
                )}
              </div>
            )}
          </div>


        </div>
        </div>
      </div>
    </>
  );
};